import { MessageSquare, Sparkles, Languages } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { useTranslation } from '@/hooks/use-translation'
import { useAgentChat } from '../hooks/use-agent-chat'
import { useAgentSessions } from '../hooks/use-agent-sessions'
import { AgentChatHeader } from './agent-chat-header'
import { AgentInputBar } from './agent-input-bar'
import { AgentMessageList } from './agent-message-list'

type Props = {
  onClose: () => void
}

export function AgentChatPanel({ onClose }: Props) {
  const t = useTranslation()
  const { messages, isStreaming, sendMessage, loadSession, startNewChat, cancel } = useAgentChat()
  const { sessions, isLoading: sessionsLoading, deleteSession } = useAgentSessions(true)

  const handleClose = useCallback(() => {
    cancel()
    onClose()
  }, [cancel, onClose])

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleClose])

  const handleSelectSession = useCallback(
    (id: string) => {
      loadSession(id)
    },
    [loadSession],
  )

  const handleDeleteSession = useCallback(
    (id: string) => {
      deleteSession(id)
    },
    [deleteSession],
  )

  const hasMessages = messages.length > 0

  const suggestions = [
    { text: t('agent.chat.suggestions.brandVoices'), icon: MessageSquare },
    { text: t('agent.chat.suggestions.contentStats'), icon: Sparkles },
    { text: t('agent.chat.suggestions.changeLanguage'), icon: Languages },
  ]

  return (
    <div className="fixed right-0 top-0 bottom-0 w-[420px] z-50 bg-card shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
      <AgentChatHeader
        onClose={handleClose}
        onNewChat={startNewChat}
        sessions={sessions}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        sessionsLoading={sessionsLoading}
      />

      {hasMessages ? (
        <AgentMessageList messages={messages} />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground text-center">
              {t('agent.chat.heading')}
            </h3>
            <p className="text-sm text-muted-foreground text-center mt-1">
              {t('agent.chat.description')}
            </p>
          </div>
          <div className="flex flex-col gap-2 w-full max-w-[280px]">
            {suggestions.map(({ text, icon: Icon }) => (
              <button
                key={text}
                onClick={() => sendMessage(text)}
                className="flex items-center gap-2.5 text-left text-sm text-card-foreground bg-muted/50 hover:bg-muted border border-border rounded-xl px-3.5 py-2.5 transition-colors duration-150"
              >
                <Icon className="w-4 h-4 text-primary shrink-0" />
                {text}
              </button>
            ))}
          </div>
        </div>
      )}

      <AgentInputBar onSend={sendMessage} disabled={isStreaming} />
    </div>
  )
}
